import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  isMarkdownVisuallyEmpty,
  trimMarkdownOuterNewlines,
} from "@/lib/markdown/markdownShared";

export type PlainDocJSON = {
  type: "doc";
  content: Array<{
    type: "paragraph";
    content?: Array<{ type: "text"; text: string } | { type: "hardBreak" }>;
  }>;
};

export const markdownToPlainDoc = (md: string): PlainDocJSON => {
  const trimmed = trimMarkdownOuterNewlines(md);
  if (isMarkdownVisuallyEmpty(trimmed)) {
    return { type: "doc", content: [{ type: "paragraph" }] };
  }
  const paragraphs = trimmed.split(/\n\n+/);
  const content = paragraphs.map((p) => {
    const parts = p.split("\n");
    const paragraphContent: Array<
      { type: "text"; text: string } | { type: "hardBreak" }
    > = [];
    for (let i = 0; i < parts.length; i++) {
      if (i > 0) paragraphContent.push({ type: "hardBreak" });
      paragraphContent.push({ type: "text", text: parts[i] ?? "" });
    }
    return { type: "paragraph" as const, content: paragraphContent };
  });
  return { type: "doc", content };
};

const countTrailingSpaces = (s: string): number =>
  (/ *$/.exec(s)?.[0] ?? "").length;

const appendHardBreakToParagraphMarkdown = (acc: string): string => {
  const trailing = countTrailingSpaces(acc);
  const pad = Math.max(0, 2 - trailing);
  return `${acc}${" ".repeat(pad)}\n`;
};

const serializePlainParagraph = (paragraph: ProseMirrorNode): string => {
  let acc = "";
  paragraph.forEach((child) => {
    if (child.isText) {
      acc += child.text ?? "";
    } else if (child.type.name === "hardBreak") {
      acc = appendHardBreakToParagraphMarkdown(acc);
    }
  });
  return acc;
};

/**
 * Serialize a ProseMirror doc (plain markdown-mode editor) back to markdown string.
 * Hard breaks match CommonMark / htmlToMarkdown: two spaces before LF when the line does not already end with two spaces.
 */
export const plainDocToMarkdown = (doc: ProseMirrorNode): string => {
  const paragraphs: string[] = [];
  doc.forEach((block) => {
    if (block.type.name === "paragraph") {
      paragraphs.push(serializePlainParagraph(block));
    }
  });
  return trimMarkdownOuterNewlines(paragraphs.join("\n\n"));
};
